import React, { createContext, useState, useContext, useEffect } from 'react';
import { RoundButton } from "../components/buttons/base/RoundButton";
import ModernHeaderButton from "../components/buttons/base/ModernHeaderButton";
import { translate } from "../core/i18n";
import { getDomainCfg, store } from "../core/state";
import { THEME, EDITOR_VIEW_NAME } from '../core/constants';
import { getCurrentViewName } from '../core/views';

interface ThemeContextType {
  theme: string;
  toggleThemeRequested: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<string>(getCurrentTheme() as string);

  const toggleThemeRequested = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setThemeState(newTheme);
    setTheme(newTheme);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Apply theme with darkModeEditorOnly check
    applyThemeToDOM(theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleThemeRequested }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const darkModeConfigured = function () {
  return getDomainCfg().viewSettings.features.darkModeEnabled ? 'dark' : 'light';
}

export const getCurrentTheme = function () {
  return localStorage.getItem('bs-theme') || darkModeConfigured();
}

export const isDarkMode = function () {
  return getCurrentTheme() === 'dark';
}

const applyThemeToDOM = (newTheme: string) => {
  // Check if darkModeEditorOnly is enabled
  const domainCfg = getDomainCfg();
  const darkModeEditorOnly = domainCfg?.viewSettings?.features?.darkModeEditorOnly;
  
  if (darkModeEditorOnly) {
    // Only apply dark theme if we're in the editor view
    const currentView = getCurrentViewName();
    if (currentView === EDITOR_VIEW_NAME && newTheme === 'dark') {
      document.documentElement.setAttribute('data-bs-theme', 'dark');
    } else {
      // Force light theme for non-editor views when darkModeEditorOnly is enabled
      document.documentElement.setAttribute('data-bs-theme', 'light');
    }
  } else {
    // Normal behavior: apply theme globally
    document.documentElement.setAttribute('data-bs-theme', newTheme);
  }
};

export const setTheme = (newTheme: string) => {  
  localStorage.setItem('bs-theme', newTheme);
  applyThemeToDOM(newTheme);
};

export const toggleTheme = function () {
  const currentTheme = getCurrentTheme();
  console.log('toggleTheme', currentTheme);
  const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
  setTheme(newTheme);
  return newTheme;
}

export const ToggleThemeButton = function () {
  const [currentTheme, setCurrentTheme] = store.useState(THEME);

  const toggleThemeRequested = () => {    
    const newTheme = toggleTheme();
    setCurrentTheme(newTheme);    
  };
  
  const sunButton = <RoundButton title={translate('sun-btn-title')} onClick={toggleThemeRequested} icon="sun" />
  const moonButton = <RoundButton title={translate('moon-btn-title')} onClick={toggleThemeRequested} icon="moon" />
  return currentTheme === 'dark' ? sunButton : moonButton;
}

export const ModernToggleThemeButton = function () {
  const [currentTheme, setCurrentTheme] = store.useState(THEME);

  const toggleThemeRequested = () => {    
    const newTheme = toggleTheme();
    setCurrentTheme(newTheme);    
  };
  
  const isDark = currentTheme === 'dark';
  return (
    <ModernHeaderButton 
      icon={isDark ? "sun" : "moon"} 
      onClick={toggleThemeRequested} 
      title={isDark ? translate('theme-toggle-light') : translate('theme-toggle-dark')} 
      variant={'secondary'}
    />
  );
}